import Vue from 'vue';
import fetch from 'unfetch';
import VueApollo from 'vue-apollo';
import { createApolloClient, restartWebsockets } from 'vue-cli-plugin-apollo/graphql-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import typeDefs from '../types.gql';
import EventBus from '@/utilities/eventBus';
import logger from 'client-logger';

// If we're in a browser that doesn't have fetch (cough cough IE), then we need to polyfill
if (!window.fetch) {
	window.fetch = fetch;
}

// Install the vue plugin
Vue.use(VueApollo);

// Name of the auth token in localStorage
export const AUTH_TOKEN = 'apollo-token';

// Http endpoint
const httpEndpoint = process.env.VUE_APP_GRAPHQL_HTTP || 'http://localhost:8080/graphql';

// Files URL root
export const filesRoot = process.env.VUE_APP_FILES_ROOT;
Vue.prototype.$filesRoot = filesRoot;

// Config
const options = {
	// You can use `https` for secure connection (recommended in production)
	httpEndpoint,
	// You can use `wss` for secure connection (recommended in production)
	// Use `null` to disable subscriptions
	wsEndpoint: process.env.VUE_APP_GRAPHQL_WS || 'ws://localhost:8080/graphql',
	// LocalStorage token
	tokenName: AUTH_TOKEN,
	// Enable Automatic Query persisting with Apollo Engine
	persisting: false,
	// Use websockets for everything (no HTTP)
	// You need to pass a `wsEndpoint` for this to work
	websocketsOnly: true,
	// Is being rendered on the server?
	ssr: false,

	// Override default apollo link
	// note: don't override httpLink here, specify httpLink options in the
	// httpLinkOptions property of defaultOptions.
	// link: myLink

	// Override default cache
	cache: new InMemoryCache(),

	// Override the way the Authorization header is set
	getAuth: () => {
		return localStorage.getItem(AUTH_TOKEN) ? localStorage.getItem(AUTH_TOKEN) : '';
	},

	// Additional ApolloClient options
	// apollo: {},

	// Client local data (see apollo-link-state)
	// clientState: { resolvers: { ... }, defaults: { ... } }

	// GraphQL schema definitions
	typeDefs
};

// Create apollo client
const { apolloClient, wsClient } = createApolloClient(options);

// Override connection params
wsClient.connectionParams = () => {
	return {
		headers: {
			authorization: localStorage.getItem(AUTH_TOKEN) ? localStorage.getItem(AUTH_TOKEN) : ''
		}
	};
};
wsClient.reconnect = true;
apolloClient.wsClient = wsClient;

// Create vue apollo provider
export const apolloProvider = new VueApollo({
	defaultClient: apolloClient,
	defaultOptions: {
		$query: {
			// fetchPolicy: 'cache-and-network',
		}
	},
	errorHandler(error) {
		logger.error({ error: error.message }, 'apolloProvider error');
	}
});

// Create a second provider that ONLY runs REST/HTTP (only necessary for operations with file uploads)
const restOptions = { ...options, websocketsOnly: false };
delete restOptions.wsEndpoint;
const httpClient = createApolloClient(restOptions);
export const httpApolloProvider = new VueApollo({
	defaultClient: httpClient.apolloClient,
	errorHandler(error) {
		logger.error({ error: error.message }, 'httpApolloProvider error');
	}
});

// Manually call this when user logs in
export async function onLogin(userToken) {
	if (typeof localStorage !== 'undefined' && userToken) {
		localStorage.setItem(AUTH_TOKEN, userToken);
	}
	if (apolloClient.wsClient) restartWebsockets(apolloClient.wsClient);
	try {
		await apolloClient.resetStore();
		await httpClient.apolloClient.resetStore();

		// Here we intercept our WebSocket's messaging system so that we can grab whatever subscription messages we want
		const originalOnMessage = apolloClient.wsClient.client.onmessage;
		apolloClient.wsClient.client.onmessage = msg => {
			const data = JSON.parse(msg.data);
			if (data.sub) {
				// If this is a subscription message, then we want to handle it directly
				EventBus.$emit(data.type, JSON.parse(data.payload));
			} else {
				// If it's just a regular GraphQL request, then let Apollo handle that
				originalOnMessage(msg);
			}
		};
	} catch (e) {
		logger.error({ error: e.message }, 'Error on cache reset (login)');
	}
}

// Manually call this when user logs out
export async function onLogout() {
	if (typeof localStorage !== 'undefined') {
		localStorage.removeItem(AUTH_TOKEN);
	}
	if (apolloClient.wsClient) restartWebsockets(apolloClient.wsClient);
	try {
		await apolloClient.resetStore();
		await httpClient.apolloClient.resetStore();
	} catch (e) {
		logger.error({ error: e.message }, 'Error on cache reset (logout)');
	}
}
